import {
  DatabaseZap,
  FileText,
  Hash,
  Laptop,
  LockKeyhole,
  ShieldCheck,
  Smartphone,
} from 'lucide-react';

import { cn } from '@/lib/utils';

function DeviceTile({
  icon: Icon,
  label,
  sub,
  status,
  className,
  delay = 0,
}: {
  icon: typeof Laptop;
  label: string;
  sub: string;
  status: string;
  className?: string;
  delay?: number;
}) {
  return (
    <div
      className={cn(
        'animate-float absolute w-[138px] rounded-xl border border-border/70 bg-card/85 p-3 shadow-[0_18px_44px_-28px_rgba(0,0,0,0.9)] backdrop-blur-md sm:w-[152px]',
        className,
      )}
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex items-center justify-between">
        <span className="grid size-7 place-items-center rounded-lg border border-border/60 bg-muted/50">
          <Icon className="size-3.5 text-foreground/80" strokeWidth={1.8} />
        </span>
        <span className="inline-flex items-center gap-1 text-[9px] font-medium uppercase tracking-wider text-primary">
          <span className="status-dot scale-75 bg-primary" />
          {status}
        </span>
      </div>
      <p className="mt-2.5 text-[12px] font-semibold leading-none">{label}</p>
      <p className="mt-1 text-[10px] leading-none text-muted-foreground">{sub}</p>
    </div>
  );
}

/**
 * Signature illustration: two devices connected by an animated flow path.
 * Pure CSS + SVG so it stays crisp and costs nothing at runtime.
 */
export function FlowVisual() {
  return (
    <div className="relative mx-auto w-full max-w-[470px]">
      <div className="absolute -inset-10 -z-10 rounded-full bg-[radial-gradient(circle_at_35%_25%,var(--glow),transparent_62%)] blur-2xl" />

      <div className="surface relative overflow-hidden rounded-[26px] p-4 sm:p-5">
        <div className="flex items-center justify-between px-1">
          <span className="text-[9px] font-semibold uppercase tracking-[0.22em] text-muted-foreground/70">
            Direct channel
          </span>
          <span className="inline-flex items-center gap-1.5 text-[10px] font-medium text-primary">
            <span className="status-dot scale-75 bg-primary" />
            End-to-end encrypted
          </span>
        </div>

        <div className="relative mt-3 h-[302px] overflow-hidden rounded-2xl border border-border/60 bg-background/50 sm:h-[326px]">
          <div
            className="pointer-events-none absolute inset-0 opacity-60"
            style={{
              backgroundImage:
                'linear-gradient(to right, var(--grid-line) 1px, transparent 1px), linear-gradient(to bottom, var(--grid-line) 1px, transparent 1px)',
              backgroundSize: '40px 40px',
              maskImage: 'radial-gradient(ellipse 75% 75% at 50% 45%, black 15%, transparent 80%)',
            }}
          />

          <DeviceTile
            icon={Laptop}
            label="This browser"
            sub="Windows · Chrome"
            status="Sending"
            className="left-4 top-5"
          />
          <DeviceTile
            icon={Smartphone}
            label="Their device"
            sub="macOS · Safari"
            status="Receiving"
            className="bottom-5 right-4"
            delay={1200}
          />

          <svg
            viewBox="0 0 400 330"
            className="absolute inset-0 size-full"
            fill="none"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="flow-path" x1="90" y1="80" x2="310" y2="250" gradientUnits="userSpaceOnUse">
                <stop stopColor="var(--brand)" stopOpacity="0.9" />
                <stop offset="0.55" stopColor="var(--brand-2)" stopOpacity="0.75" />
                <stop offset="1" stopColor="var(--brand-3)" stopOpacity="0.5" />
              </linearGradient>
            </defs>
            <path
              d="M104 92 C 214 102, 158 228, 298 244"
              stroke="url(#flow-path)"
              strokeWidth="1.6"
              strokeLinecap="round"
              className="animate-beam"
            />
            <path
              d="M104 92 C 214 102, 158 228, 298 244"
              stroke="var(--foreground)"
              strokeOpacity="0.08"
              strokeWidth="1"
              strokeLinecap="round"
            />
          </svg>

          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            <div className="relative">
              <div className="absolute -inset-4 animate-pulse rounded-full bg-primary/10 blur-xl" />
              <div className="relative flex items-center gap-2.5 rounded-xl border border-primary/25 bg-card/95 px-3 py-2 shadow-[0_16px_40px_-20px_rgba(0,0,0,0.8)] backdrop-blur">
                <span className="grid size-8 place-items-center rounded-lg bg-primary/12 text-primary">
                  <FileText className="size-4" strokeWidth={1.8} />
                </span>
                <div className="pr-1">
                  <p className="text-[11px] font-semibold leading-none">brand-film.mp4</p>
                  <p className="mt-1 font-mono text-[9px] leading-none text-muted-foreground">
                    248 MB · 64 KB chunks
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="absolute bottom-4 left-4 inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-card/80 px-2.5 py-1 text-[9px] font-medium text-muted-foreground backdrop-blur">
            <LockKeyhole className="size-2.5 text-primary" />
            DTLS encrypted
          </div>
        </div>

        <div className="mt-3 grid grid-cols-3 gap-2">
          {[
            { icon: ShieldCheck, label: 'DTLS encrypted' },
            { icon: DatabaseZap, label: '0 bytes stored' },
            { icon: Hash, label: 'SHA-256' },
          ].map((item) => (
            <div
              key={item.label}
              className="flex items-center justify-center gap-1.5 rounded-lg border border-border/50 bg-muted/25 px-2 py-1.5 text-[9px] font-medium text-muted-foreground"
            >
              <item.icon className="size-3 text-primary/80" />
              {item.label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
